// Site picker for the card-grid pages: the Asset ID Creator and the IEA Survey site lists.
// Mockup: MOCKUP_SITE_PICKER.html rev 2 (Robert, 2026-10-22: card grids only, no grouping; approved to build 8:26 AM).
// Search first, On This iPad first, filter chips for that page's jobs, then every other site as one compact row,
// in site number order. Dropdown pages keep their dropdowns.
//
// Pure HTML plus a small wire-up: typing and chip taps filter the rows in place, with no page redraw,
// so the search box keeps its place and the keyboard stays up on the iPad.

export interface PickerSite {
  siteNumber: string;
  name: string;
  href: string;
  /** One short line under the name: "133 assets". */
  sub: string;
  /** Status pills, already built by the page. */
  pills: string;
  /** A site pack is saved on this iPad: the site goes in On This iPad. */
  onDevice: boolean;
  /** Which filter chips the site answers to, by key. */
  flags: readonly string[];
}

export interface PickerFilter {
  key: string;
  label: string;
}

export interface PickerView {
  /** One picker per page: "assets" or "survey". Search and chip are kept per picker while the app is open. */
  id: string;
  title: string;
  sub: string;
  sites: PickerSite[];
  filters: PickerFilter[];
  /** Above the title: the Drive line on the Creator. */
  above?: string;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

/** What a site is searched by: number and name. */
export function searchText(s: Pick<PickerSite, "siteNumber" | "name">): string {
  return `${s.siteNumber} ${s.name}`.toLowerCase();
}

/** A site shows when it matches every word typed and the chip picked. */
export function siteMatches(s: Pick<PickerSite, "siteNumber" | "name" | "flags">, query: string, filter: string): boolean {
  if (filter && filter !== "all" && !s.flags.includes(filter)) return false;
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const text = searchText(s);
  return words.every((w) => text.includes(w));
}

/** Search and chip, kept while the app is open, so a redraw or coming back keeps them. */
const state = new Map<string, { query: string; filter: string }>();
export function pickerState(id: string): { query: string; filter: string } {
  let s = state.get(id);
  if (!s) state.set(id, (s = { query: "", filter: "all" }));
  return s;
}

function rowHtml(s: PickerSite, shown: boolean): string {
  return `<a class="sp-row" href="${esc(s.href)}" data-sp-text="${esc(searchText(s))}" data-sp-flags="${esc(s.flags.join(" "))}"${shown ? "" : " hidden"}>
    <span class="sp-num">${esc(s.siteNumber)}</span>
    <span class="sp-t"><b>${esc(s.name)}</b><small>${esc(s.sub)}</small></span>
    <span class="sp-pills">${s.pills}</span></a>`;
}

function groupHtml(key: string, title: string, sites: PickerSite[], query: string, filter: string): string {
  if (!sites.length) return "";
  const shown = sites.filter((s) => siteMatches(s, query, filter)).length;
  return `<div class="sp-grp" data-sp-grp="${key}"${shown ? "" : " hidden"}>
    <div class="sp-ghd">${esc(title)} <small data-sp-count>${shown} ${shown === 1 ? "site" : "sites"}</small></div>
    ${sites.map((s) => rowHtml(s, siteMatches(s, query, filter))).join("")}</div>`;
}

export function sitePicker(v: PickerView): string {
  const { query, filter } = pickerState(v.id);
  const sites = [...v.sites].sort((a, b) => a.siteNumber.localeCompare(b.siteNumber));
  const chips = [{ key: "all", label: "All" }, ...v.filters]
    .map((f) => `<button type="button" class="sp-chip${filter === f.key ? " on" : ""}" data-sp-filter="${esc(f.key)}">${esc(f.label)}</button>`)
    .join("");
  const any = sites.some((s) => siteMatches(s, query, filter));
  return `${v.above ?? ""}<div class="sp" data-sp="${esc(v.id)}">
    <div><h2 class="sv-h">${esc(v.title)}</h2><div class="sv-sub">${esc(v.sub)}</div></div>
    <input class="sp-search" type="search" data-sp-search value="${esc(query)}" placeholder="Search by name or number" autocomplete="off" autocapitalize="off" spellcheck="false" enterkeyhint="search" aria-label="Search sites">
    ${v.filters.length ? `<div class="sp-chips">${chips}</div>` : ""}
    ${groupHtml("device", "On This iPad", sites.filter((s) => s.onDevice), query, filter)}
    ${groupHtml("all", "All Sites", sites.filter((s) => !s.onDevice), query, filter)}
    <div class="sp-none" data-sp-none${any ? " hidden" : ""}>No site matches. Check the spelling or the number.</div>
  </div>`;
}

/** Filters the rows already on the page, with no redraw. */
function apply(box: HTMLElement): void {
  const { query, filter } = pickerState(box.dataset.sp ?? "");
  let any = false;
  for (const g of box.querySelectorAll<HTMLElement>("[data-sp-grp]")) {
    let n = 0;
    for (const r of g.querySelectorAll<HTMLElement>(".sp-row")) {
      const ok = siteMatches({ siteNumber: "", name: r.dataset.spText ?? "", flags: (r.dataset.spFlags ?? "").split(" ").filter(Boolean) }, query, filter);
      r.hidden = !ok;
      if (ok) n++;
    }
    g.hidden = n === 0;
    const c = g.querySelector<HTMLElement>("[data-sp-count]");
    if (c) c.textContent = `${n} ${n === 1 ? "site" : "sites"}`;
    any = any || n > 0;
  }
  const none = box.querySelector<HTMLElement>("[data-sp-none]");
  if (none) none.hidden = any;
  for (const c of box.querySelectorAll<HTMLElement>("[data-sp-filter]")) c.classList.toggle("on", c.dataset.spFilter === filter);
}

let wired = new WeakSet<HTMLElement>();
/** Once per page root. Safe to call on every draw. */
export function wireSitePicker(root: HTMLElement): void {
  if (wired.has(root)) return;
  wired.add(root);
  root.addEventListener("input", (ev) => {
    const input = (ev.target as HTMLElement).closest<HTMLInputElement>("[data-sp-search]");
    const box = input?.closest<HTMLElement>("[data-sp]");
    if (!input || !box) return;
    pickerState(box.dataset.sp ?? "").query = input.value;
    apply(box);
  });
  root.addEventListener("click", (ev) => {
    const chip = (ev.target as HTMLElement).closest<HTMLElement>("[data-sp-filter]");
    const box = chip?.closest<HTMLElement>("[data-sp]");
    if (!chip || !box) return;
    pickerState(box.dataset.sp ?? "").filter = chip.dataset.spFilter ?? "all";
    apply(box);
  });
}

/** After a page redraw: put the caret back in the search box if it was there, so typing is never cut off. */
export function keepSearchFocus(root: HTMLElement, wasTyping: boolean, caret: number | null): void {
  if (!wasTyping) return;
  const input = root.querySelector<HTMLInputElement>("[data-sp-search]");
  if (!input) return;
  input.focus({ preventScroll: true });
  if (caret !== null) input.setSelectionRange(caret, caret);
}

/** Read before a redraw: is the person typing in the search box, and where. */
export function searchFocus(root: HTMLElement): { wasTyping: boolean; caret: number | null } {
  const a = document.activeElement as HTMLInputElement | null;
  const typing = !!a && root.contains(a) && a.matches("[data-sp-search]");
  return { wasTyping: typing, caret: typing ? a!.selectionStart : null };
}

/** Tests only. */
export function resetPickerForTests(): void {
  state.clear();
  wired = new WeakSet();
}
